import './EncourageEmailConfirm.scss';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { DisableBodyScroll } from '../../../../hooks/useDisableBodyScroll';
import useCurrentUser from '../../../../hooks/useCurrentUser';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toastError } from '../../../../libs/toast';
import { CURRENT_USER, MAIL } from '../../../../const/toasts';
import {
  sendEnrollmentEmailCurrentUser,
  updateEmailCurrentUser,
  USER_CURRENT_KEY,
} from '../../../../api/public-voissa/users';
import Group from '../../UI/Group/Group';
import Button, { BUTTON_VARIANTS } from '../../UI/Button/Button';
import Input from '../../UI/Input/Input';
import logoSrc from '../../../../images/logo-full.svg';

const STORAGE_KEY = 'encourage_email_confirm';

export function isEncourageEmailConfirmClosed() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === 'closed';
  } catch {
    return false;
  }
}

/**
 * Composant monté par le ModalManager.
 * - forceOpen=false (ouverture auto) : à la fermeture on écrit 'closed' en sessionStorage
 * - forceOpen=true (CTA) : à la fermeture on N'ÉCRIT PAS 'closed' (modifiable selon ton besoin)
 */
const EncourageEmailConfirm = ({ forceOpen = false, handleClose = () => {} }) => {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const email = currentUser?.email?.address || '';

  const [currentEmail, setCurrentEmail] = useState(email);
  const [toggleUpdateEmail, setToggleUpdateEmail] = useState(false);
  const [isSendMail, setIsSendMail] = useState(false);
  const [isSendMailLoading, setIsSendMailLoading] = useState(false);

  const { mutate: mutateSendEnrollmentEmail } = useMutation(
    () => sendEnrollmentEmailCurrentUser(),
    { onError: () => toastError(MAIL.ERROR.SEND_ENROLLMENT) }
  );

  const {
    mutate: mutateUpdateEmail,
    error: errorUpdateEmail,
    isError: isErrorUpdateEmail,
    isLoading: isLoadingUpdateEmail,
  } = useMutation((data) => updateEmailCurrentUser(data), {
    onSuccess: async () => {
      setIsSendMailLoading(false);
      await queryClient.invalidateQueries([USER_CURRENT_KEY]);
    },
    onError: () => toastError(CURRENT_USER.ERROR.UPDATE),
  });

  function sendMail() {
    if (!isSendMail) {
      mutateSendEnrollmentEmail();
      setIsSendMail(true);
      setIsSendMailLoading(true);
      setTimeout(() => setIsSendMailLoading(false), 800);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    mutateUpdateEmail({ email: currentEmail });
  }

  const _handleClose = () => {
    if (!forceOpen) {
      try {
        sessionStorage.setItem(STORAGE_KEY, 'closed');
      } catch {}
    }
    handleClose();
  };

  return ReactDOM.createPortal(
    <div className="encourage-email-confirm-container">
      <DisableBodyScroll />
      <div className="encourage-email-confirm">
        <div className="encourage-close" onClick={_handleClose} aria-label="Fermer">
          <FontAwesomeIcon icon={faXmark} />
        </div>

        <div className="logo-wrapper">
          <img src={logoSrc} alt="Voissa Logo" className="logo" />
        </div>

        <h1>
          Bienvenue <br />
          <b>{currentUser?.username} 🎉</b>
        </h1>

        <p>
          Vous devez confirmer votre inscription en cliquant sur le lien de vérification envoyé à <b>{email}</b>.
        </p>
        <p style={{ marginTop: 12 }}>
          Pas reçu ? Écrivez à <a href="mailto:assistance@voissa.com">assistance@voissa.com</a>
        </p>

        <form className="encourage-footer" onSubmit={handleSubmit}>
          {toggleUpdateEmail ? (
            <>
              <Input
                type="email"
                required
                value={currentEmail}
                label="Votre nouvelle adresse email"
                onChange={(e) => setCurrentEmail(e.target.value)}
                error={isErrorUpdateEmail ? errorUpdateEmail?.message || "Votre adresse email n'est pas valide" : false}
                placeholder="Votre Adresse Email"
              />
              <Group right style={{ marginTop: 8 }}>
                <Button
                  onClick={() => {
                    setToggleUpdateEmail(false);
                    setCurrentEmail(email);
                  }}
                >
                  Retour
                </Button>
                <Button
                  variant={BUTTON_VARIANTS.CTA_LIGHT}
                  disabled={currentEmail.trim().length === 0}
                  type="submit"
                  loading={isLoadingUpdateEmail}
                >
                  Enregistrer
                </Button>
              </Group>
            </>
          ) : (
            <Group right className="email-unconfirmed-form-group">
              <Button
                variant={BUTTON_VARIANTS.CTA_LIGHT}
                loading={isSendMailLoading}
                disabled={isSendMail}
                onClick={sendMail}
              >
                {isSendMail ? 'Lien envoyé ✔' : 'Renvoyer le lien'}
              </Button>
              <Button variant={BUTTON_VARIANTS.CTA_LIGHT} onClick={() => setToggleUpdateEmail(true)}>
                Modifier l'email
              </Button>
            </Group>
          )}
        </form>
      </div>
    </div>,
    document.body
  );
};

export default EncourageEmailConfirm;
